import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Link,
  Chip,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ControlsBar from "./ControlsBar";
import { useLanguage } from "../contexts/LanguageContext";
import posts from "../blog/posts";
import SEOHead from "./SEOHead";

const Blog: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { isRTL } = useLanguage();

  return (
    <>
      <SEOHead
        title="Resume & Career Blog | Resume Optimizer AI"
        description="Practical resume and career advice for job seekers in the UAE. Tips on ATS optimization, Dubai job market insights, and how to write a standout resume."
        keywords="resume tips UAE, Dubai jobs blog, career advice UAE, ATS resume tips, job search Dubai, resume writing guide"
        canonicalUrl="https://my-aitools.online/resume-optimizer/#/blog"
        ogType="website"
      />
      <ControlsBar />
      <Box sx={{ minHeight: "100vh", backgroundColor: "background.default", py: 6 }}>
        <Container maxWidth="md">
          {/* Back link */}
          <Link
            href="#/"
            underline="hover"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 4, color: "primary.main", fontWeight: 600, fontSize: "0.95rem" }}
          >
            {isRTL ? "→" : "←"} Back to Resume Optimizer
          </Link>

          {/* Header */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.02em", mb: 1 }}>
              Resume &amp; Career Blog
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Practical advice for job seekers in the UAE and beyond.
            </Typography>
          </Box>

          {/* Posts */}
          <Stack spacing={3}>
            {posts.map((post) => (
              <Paper
                key={post.slug}
                component={Link}
                href={`#/blog/${post.slug}`}
                underline="none"
                sx={{
                  p: { xs: 3, sm: 4 },
                  borderRadius: 3,
                  backgroundColor: "background.paper",
                  display: "block",
                  transition: "all 0.2s ease-in-out",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: isDark
                      ? "0 8px 24px rgba(0,0,0,0.4)"
                      : "0 8px 24px rgba(79,48,29,0.12)",
                  },
                }}
              >
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                  <Chip
                    label={post.category}
                    size="small"
                    sx={{
                      backgroundColor: isDark ? "rgba(255,180,106,0.15)" : "rgba(255,132,40,0.1)",
                      color: "primary.main",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {post.date} · {post.readTime}
                  </Typography>
                </Stack>

                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 1, color: "text.primary", letterSpacing: "-0.01em" }}
                >
                  {post.title}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {post.excerpt}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ mt: 2, color: "primary.main", fontWeight: 600 }}
                >
                  Read article →
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Blog;
